import React, { useMemo, useCallback, useEffect, useRef } from "react";
import AutoResizeTextArea from "atoms/forms/auto-resize-text-area";
import { ChecklistItemRecord } from "models/view-models/block-configurations/checklist-item-record";
import { t } from "utilities/localization-utils";
import ChecklistStatusBadge from "./checklist-status-badge";
import { ChecklistStatus } from "models/enumerations/notebooks/checklist-item-status";
import { useReadonlyContext } from "utilities/contexts/readonly/use-readonly-context";
import ChecklistItemNoteModal from "organisms/checklist-item-notes/checklist-item-note-modal";
import useModalActions from "utilities/hooks/use-modal-actions";
import { ChecklistItemNoteDisplay } from "organisms/checklist-item-notes/checklist-item-note-display";
import DeprecatedShow from "atoms/wrappers/show";
import DeprecatedHide from "atoms/wrappers/hide";
import { ChecklistItemNoteRecord } from "models/view-models/checklist-item-notes/checklist-item-note-record";
import { OpenChecklistItemNoteModalButton } from "organisms/checklist-item-notes/open-checklist-item-note-modal-button";
import usePageLimits from "pages/notebooks/use-page-limits";
import RichTextEditorFooter from "molecules/rich-text/rich-text-editor-footer";
import { usePageIsViewOnly } from "pages/notebooks/use-page-is-view-only";

interface ChecklistItemDisplayProps {
    index: number;
    isEditing: boolean;
    isNumbered: boolean;
    item: ChecklistItemRecord;
    onChecklistItemNoteUpdate?: (updatedNote?: ChecklistItemNoteRecord) => void;
    onKeyDown?: (
        event: React.KeyboardEvent<HTMLTextAreaElement>,
        index: number
    ) => void;
    onTextChange?: (newText: string) => void;
    onUpdateChecklistItemChecked: () => void;
    onUpdateChecklistItemStatus?: (newStatus: ChecklistStatus) => void;
    onUpdateChecklistItemText?: (newText: string) => void;
    printable: boolean;
    showStatus: boolean;
    totalItems: number;
    saveInProgress?: boolean;
}

const CSS_CLASS_NAME = "c-checklist-item-display";

const ChecklistItemDisplay: React.FC<ChecklistItemDisplayProps> = ({
    index,
    isEditing = false,
    isNumbered,
    item,
    onChecklistItemNoteUpdate,
    onKeyDown,
    onTextChange,
    onUpdateChecklistItemChecked,
    onUpdateChecklistItemStatus,
    onUpdateChecklistItemText,
    printable = false,
    showStatus,
    totalItems,
    saveInProgress = false,
}) => {
    const { isReadonly } = useReadonlyContext();
    const { isViewOnly } = usePageIsViewOnly();
    const { handleOpen, handleClose, isOpen } = useModalActions();
    const textAreaTextRef = useRef(item.text);
    const { pageLimits } = usePageLimits();
    const checklistNumberDigitCount = useMemo(() => {
        let digits = 0;
        let length = totalItems;
        while (Math.floor(length) > 0) {
            digits++;
            length /= 10;
        }
        return Math.max(2, digits);
    }, [totalItems]);

    const cssClassName = useMemo(() => {
        const classNames = [CSS_CLASS_NAME];

        if (printable) {
            classNames.push("-printable");
        }

        if (showStatus) {
            classNames.push("-stackOnMobile");
        }

        return classNames.join(" ");
    }, [printable, showStatus]);

    const padLeftWithZeros = useCallback(
        (number: number): string => {
            return number.toString().padStart(checklistNumberDigitCount, "0");
        },
        [checklistNumberDigitCount]
    );

    const handleNoteDelete = (): void => {
        onChecklistItemNoteUpdate?.();
    };

    const handleNoteSave = (updatedNote: ChecklistItemNoteRecord): void => {
        onChecklistItemNoteUpdate?.(updatedNote);
    };

    const handleTextAreaBlur = (
        event: React.FocusEvent<HTMLTextAreaElement, Element>
    ): void => {
        if (textAreaValue === lastSavedText) {
            return;
        }
        onUpdateChecklistItemText?.(event.target.value);
        setLastSavedText(event.target.value);
    };

    const [lastSavedText, setLastSavedText] = React.useState(item.text);
    const [textAreaValue, setTextAreaValue] = React.useState(item.text);

    const handleTextAreaKeyDown = (
        event: React.KeyboardEvent<HTMLTextAreaElement>
    ): void => {
        onKeyDown?.(event, index);
    };

    useEffect(() => {
        if (item.text !== textAreaTextRef.current) {
            textAreaTextRef.current = item.text;
            setTextAreaValue(item.text);
            setLastSavedText(item.text);
        }
    }, [item.text]);

    const handleTextAreaChange = (
        ev: React.ChangeEvent<HTMLTextAreaElement>
    ): void => {
        if (onTextChange == null) {
            return;
        }

        const newValue = ev.target.value;
        setTextAreaValue(newValue);
        onTextChange?.(newValue);

        setLastSavedText(textAreaValue);
    };

    return (
        <>
            <div className={cssClassName}>
                <div className={`${CSS_CLASS_NAME}__item-state-wrapper`}>
                    <DeprecatedShow when={isNumbered}>
                        <p className={`${CSS_CLASS_NAME}__number`}>
                            {padLeftWithZeros(index + 1)}
                        </p>
                    </DeprecatedShow>
                    <DeprecatedShow when={showStatus}>
                        <div className={`${CSS_CLASS_NAME}__status`}>
                            <ChecklistStatusBadge
                                disabled={isViewOnly}
                                onClick={onUpdateChecklistItemStatus}
                                status={item.status}
                            />
                        </div>
                    </DeprecatedShow>
                    <DeprecatedHide when={showStatus}>
                        <input
                            checked={item.isChecked}
                            className={`${CSS_CLASS_NAME}__checkbox`}
                            disabled={isViewOnly}
                            id={`item-checkbox-${index}`}
                            onChange={onUpdateChecklistItemChecked}
                            type="checkbox"
                            maxLength={pageLimits?.checklistBlockCharacterLimit}
                        />
                    </DeprecatedHide>
                </div>
                <div className={`${CSS_CLASS_NAME}__text-container`}>
                    <div
                        className={`${CSS_CLASS_NAME}__text-area-and-note-modal-trigger`}>
                        <div className={`${CSS_CLASS_NAME}__text-area`}>
                            <DeprecatedShow when={isEditing}>
                                <AutoResizeTextArea
                                    disabled={saveInProgress}
                                    cssClassName={`${CSS_CLASS_NAME}__text-content`}
                                    id={`item-text-${index}`}
                                    onBlur={handleTextAreaBlur}
                                    onChange={handleTextAreaChange}
                                    onKeyDown={handleTextAreaKeyDown}
                                    placeholder={t(
                                        "input-placeholder-promptToType"
                                    )}
                                    value={textAreaValue}
                                    maxLength={
                                        pageLimits?.checklistBlockCharacterLimit
                                    }
                                />
                                <RichTextEditorFooter
                                    baseClassName={CSS_CLASS_NAME}
                                    currentLength={item.text.length}
                                    maxLength={
                                        pageLimits?.checklistBlockCharacterLimit
                                    }
                                />
                            </DeprecatedShow>
                            <DeprecatedHide when={isEditing}>
                                <div
                                    className={`${CSS_CLASS_NAME}__text-content`}
                                    dangerouslySetInnerHTML={{
                                        __html: item.text,
                                    }}></div>
                            </DeprecatedHide>
                        </div>
                        <DeprecatedHide when={isReadonly}>
                            <OpenChecklistItemNoteModalButton
                                cssClassName={`${CSS_CLASS_NAME}__note-button`}
                                handleOpen={handleOpen}
                                noteId={item.note?.id ?? 0}
                            />
                        </DeprecatedHide>
                    </div>
                    <DeprecatedHide when={item.note == null}>
                        <ChecklistItemNoteDisplay
                            note={item.note!}
                            printable={printable}
                        />
                    </DeprecatedHide>
                </div>
            </div>
            <ChecklistItemNoteModal
                checklistItemId={item.id}
                closeDialog={handleClose}
                existingChecklistItemNote={item.note}
                isVisible={isOpen}
                onDelete={handleNoteDelete}
                onSave={handleNoteSave}
            />
        </>
    );
};

export default ChecklistItemDisplay;
